import { LinkButton, DeprecatedVisibilityOffIcon, DeprecatedVisibilityOnIcon } from '@arnold/common';
import { Form, Formik, FormikErrors, FormikHelpers } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { FormControl, FormGroup } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';
import { FormControlPassword, FormErrorFeedback, FormGroupLabel } from '../../components/Common';
import { cookieConsentManager } from '../../components/CookieConsent';
import { LinkWrap, ShowPasswordButton } from '../../components/StyledComponents';
import { SsoLoginMode } from '../../generated/hooks';
import { redirectToSsoLogin } from '../Sso/sso';
import { normalizeSsoEmail, resolveSsoLoginMode, useSsoLoginMode } from '../Sso/useSsoLoginMode';
import { ColoredBodySmall, FullWidthButton } from './Common';

interface IProps {
  lang?: string;
  defaultEmail: string;
  redirect?: string | null;
  handleSignIn: (
    email: string,
    password: string,
    otp: string | undefined,
    setErrors: (errors: FormikErrors<IFormValues>) => void,
    setSubmitting: (isSubmitting: boolean) => void,
  ) => void;
  handleResetPassword: (email: string) => void;
}

interface IFormValues {
  email: string;
  password: string;
  responseError?: string;
  emailError?: string;
}

const LoginForm = ({ lang, handleSignIn, defaultEmail, handleResetPassword, redirect }: IProps) => {
  const { t, i18n } = useTranslation('loginForm');
  const [showPassword, setShowPassword] = useState(false);
  const [currentEmail, setCurrentEmail] = useState(defaultEmail);
  const ssoLoginModeState = useSsoLoginMode(currentEmail);
  const ssoRequired = ssoLoginModeState.value === SsoLoginMode.Required;
  const ssoOptional = ssoLoginModeState.value === SsoLoginMode.Optional;

  useEffect(() => {
    setCurrentEmail(defaultEmail);
  }, [defaultEmail]);

  const LoginSchema = useMemo(
    () =>
      Yup.object().shape({
        email: Yup.string().email(t('invalidEmail')).required(t('fillEmail')),
        password: ssoRequired ? Yup.string() : Yup.string().required(t('fillPassword')),
      }),
    [ssoRequired, t],
  );

  const initialValues: IFormValues = {
    email: defaultEmail,
    password: '',
    responseError: undefined,
    emailError: undefined,
  };

  const handleSubmit = async (values: any, { setErrors, setSubmitting }: FormikHelpers<IFormValues>) => {
    const ssoLoginMode = await resolveSsoLoginMode(ssoLoginModeState);
    if (ssoLoginMode === SsoLoginMode.Required) {
      setSubmitting(false);
      redirectToSsoLogin(normalizeSsoEmail(values.email), redirect);
      return;
    }
    await handleSignIn(values.email, values.password, undefined, setErrors, setSubmitting);
  };

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={LoginSchema}>
      {({ errors, handleChange, handleSubmit, isSubmitting, values }) => (
        <Form noValidate={true} onSubmit={handleSubmit} data-cy="login-form">
          <FormGroup>
            <FormGroupLabel data-icom="login-email-label">{t('email')}</FormGroupLabel>
            <FormControl
              type="email"
              id="email"
              name="email"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="email"
              value={values.email || ''}
              onChange={(e) => {
                handleChange(e);
                setCurrentEmail(e.target.value);
              }}
              isInvalid={!!errors.responseError || !!errors.email}
            />
            {errors.email && <FormErrorFeedback error={errors.email} />}
          </FormGroup>
          {!ssoRequired && (
            <FormGroup>
              <FormGroupLabel>{t('password')}</FormGroupLabel>
              <FormControlPassword
                style={{
                  paddingRight: 40,
                }}
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                autoComplete="current-password"
                value={values.password || ''}
                onChange={handleChange}
                isInvalid={!!errors.responseError || !!errors.password}
              />
              <ShowPasswordButton
                onClick={(e) => {
                  if (e.screenX || e.screenY) {
                    e.preventDefault();
                    setShowPassword(!showPassword);
                  }
                }}
              >
                {showPassword ? <DeprecatedVisibilityOffIcon /> : <DeprecatedVisibilityOnIcon />}
              </ShowPasswordButton>
              {errors.password && <FormErrorFeedback error={errors.password} />}
              {errors.responseError && <FormErrorFeedback error={errors.responseError} />}
            </FormGroup>
          )}
          <ColoredBodySmall className="mb-4">
            <Trans
              i18nKey={'loginForm:loginTermsNotice'}
              components={[<a href={t('registration:T&CLink')} target="_blank" rel="noreferrer" />]}
            />
          </ColoredBodySmall>
          <div className="text-center">
            {ssoOptional && (
              <FullWidthButton
                variant="primary"
                type="button"
                className="mb-3"
                disabled={isSubmitting || !!errors.email}
                onClick={() => redirectToSsoLogin(normalizeSsoEmail(values.email), redirect)}
                data-icom="sso-login"
                data-cy="sso-login"
              >
                {t('loginWithSso')}
              </FullWidthButton>
            )}
            <FullWidthButton
              variant={ssoOptional ? 'outline-primary' : 'primary'}
              type="submit"
              disabled={isSubmitting || !!errors.email || (!ssoRequired && !!errors.password)}
              data-icom="login-submit"
              data-cy="login-submit"
              className="ml-0"
            >
              {ssoRequired ? t('loginWithSso') : t('signIn')}
            </FullWidthButton>
            {!ssoRequired && (
              <LinkWrap>
                <LinkButton
                  data-cy={'admin-reset-password-button'}
                  onClick={() => handleResetPassword(values.email)}
                  disabled={!values.email || !!errors.email}
                >
                  {t('setupNewPassword')}
                </LinkButton>
              </LinkWrap>
            )}
          </div>
          <ColoredBodySmall className="mt-9">
            {t('dontHaveAccount')} <Link to={`/register/${i18n.language}?email=${values.email}`}>{t('signUp')}</Link>
          </ColoredBodySmall>
          <ColoredBodySmall className="mt-4">
            <a
              href=""
              onClick={(e) => {
                // @ts-ignore
                cookieConsentManager?.showPreferences();
                e.preventDefault();
                return;
              }}
            >
              {t('Navbar:cookieSettings')}
            </a>
          </ColoredBodySmall>
        </Form>
      )}
    </Formik>
  );
};

export default LoginForm;
